/**
 * EventDetailPage — `/events/:id`. Tab bar with Dashboard + entity data tabs.
 * Uses /entities endpoint for rich plan/attraction/kiosk data.
 * Days tab uses /extras daily_breakdown.
 */

import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import { ArrowLeft, Brain } from 'lucide-react'
import { SkeletonPage } from '@/components/ui/Skeleton'
import { Dashboard } from '../components/Dashboard'
import { PageLayout } from '../components/PageLayout'
import { useAuth } from '../contexts/AuthContext'
import { MMTable, type MMColumn } from '../components/analytics/MMTable'
import { formatDollars, formatNumber } from '../lib/formatters'
import { useEntityData } from '../hooks/useEntityData'

const TABS = ['dashboard', 'plans', 'attractions', 'kiosks', 'days'] as const
type Tab = (typeof TABS)[number]

const TAB_LABELS: Record<Tab, string> = {
  dashboard: 'Dashboard',
  plans: 'Plans',
  attractions: 'Attractions',
  kiosks: 'Kiosks',
  days: 'Days',
}

function perfBadge(p: string | null | undefined) {
  if (p === 'over') return '🟢'
  if (p === 'under') return '🔴'
  if (p === 'average') return '🟡'
  return ''
}

const planColumns: MMColumn[] = [
  { key: 'plan_name', label: 'Plan' },
  { key: 'plan_type', label: 'Type' },
  { key: 'price', label: 'Price', align: 'right', format: (v) => v != null ? formatDollars(Number(v)) : '—' },
  { key: 'units_sold', label: 'Sold', align: 'right', format: (v) => formatNumber(Number(v)) },
  { key: 'revenue', label: 'Revenue', align: 'right', format: (v) => formatDollars(Number(v)) },
]

const attractionColumns: MMColumn[] = [
  { key: 'attraction_name', label: 'Attraction' },
  { key: 'attraction_type', label: 'Type' },
  { key: 'total_scans', label: 'Scans', align: 'right', format: (v) => formatNumber(Number(v)) },
  { key: 'unique_guests', label: 'Guests', align: 'right', format: (v) => formatNumber(Number(v)) },
  { key: 'revenue', label: 'Revenue', align: 'right', format: (v) => formatDollars(Number(v)) },
  { key: 'performance', label: '', align: 'center', format: (v) => perfBadge(v as string) },
]

const kioskColumns: MMColumn[] = [
  { key: 'kiosk_name', label: 'Kiosk' },
  { key: 'kiosk_type', label: 'Type' },
  { key: 'orders', label: 'Orders', align: 'right', format: (v) => formatNumber(Number(v)) },
  { key: 'revenue', label: 'Revenue', align: 'right', format: (v) => formatDollars(Number(v)) },
  { key: 'orders_per_hour', label: 'Orders/Hr', align: 'right', format: (v) => String(v ?? '—') },
]

interface DailyRow { day: string; scans: number; guests: number; revenue: number; paid_scans: number }

const dayColumns: MMColumn[] = [
  { key: 'day', label: 'Day', format: (v) => {
    const d = new Date(String(v))
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' })
  }},
  { key: 'revenue', label: 'Revenue', align: 'right', format: (v) => formatDollars(Number(v)) },
  { key: 'guests', label: 'Guests', align: 'right', format: (v) => formatNumber(Number(v)) },
  { key: 'scans', label: 'Scans', align: 'right', format: (v) => formatNumber(Number(v)) },
  { key: 'paid_scans', label: 'Paid Scans', align: 'right', format: (v) => formatNumber(Number(v)) },
]

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>()
  const eventId = id ? Number(id) : null
  const { events, setCurrentEventId } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab') ?? 'dashboard'
  const tab: Tab = (TABS as readonly string[]).includes(tabParam) ? (tabParam as Tab) : 'dashboard'

  const { data: entityData, loading: entityLoading } = useEntityData(eventId)

  const [dailyRows, setDailyRows] = useState<DailyRow[]>([])
  const [dailyLoading, setDailyLoading] = useState(false)

  const event = useMemo(() => events.find(e => e.EventID === eventId), [events, eventId])

  useEffect(() => { if (eventId) setCurrentEventId(eventId) }, [eventId, setCurrentEventId])

  useEffect(() => {
    if (!eventId || tab !== 'days') return
    let cancelled = false
    setDailyLoading(true)
    fetch(`/api/dashboard/${eventId}/extras`, { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) { setDailyRows(d?.daily_breakdown ?? []); setDailyLoading(false) } })
      .catch(() => { if (!cancelled) setDailyLoading(false) })
    return () => { cancelled = true }
  }, [eventId, tab])

  const setTab = (next: Tab) => {
    const sp = new URLSearchParams(searchParams)
    if (next === 'dashboard') sp.delete('tab')
    else sp.set('tab', next)
    setSearchParams(sp, { replace: true })
  }

  const dataLoading = tab === 'days' ? dailyLoading : entityLoading

  const renderTab = () => {
    if (tab === 'dashboard') {
      return <Dashboard eventId={eventId} authToken={null} />
    }
    if (dataLoading) {
      return <SkeletonPage />
    }
    switch (tab) {
      case 'plans':
        return <MMTable columns={planColumns} rows={entityData?.plans ?? []} defaultSort="revenue" emptyMessage="No plan data — run ETL to populate." />
      case 'attractions':
        return <MMTable columns={attractionColumns} rows={entityData?.attractions ?? []} defaultSort="total_scans" emptyMessage="No attraction data — run ETL to populate." />
      case 'kiosks':
        return <MMTable columns={kioskColumns} rows={entityData?.kiosks ?? []} defaultSort="revenue" emptyMessage="No kiosk data available." />
      case 'days':
        return <MMTable columns={dayColumns} rows={dailyRows} defaultSort="day" emptyMessage="No daily breakdown available." />
      default:
        return null
    }
  }

  return (
    <PageLayout
      title={event?.EventName ?? `Event ${eventId ?? ''}`}
      subtitle={event ? `EventID ${event.EventID}` : 'Event not found.'}
      actions={
        <div className="flex gap-2">
          {eventId && (
            <Link to={`/analytics/intelligence/${eventId}`}
              className="inline-flex items-center gap-1 rounded-card border border-butler-600/30 bg-butler-600/5 px-2.5 py-1.5 text-xs font-medium text-butler-600 hover:bg-butler-600/10">
              <Brain className="h-3 w-3" /> Intelligence
            </Link>
          )}
          <Link to="/events"
            className="inline-flex items-center gap-1 rounded-card border border-hairline bg-white px-2.5 py-1.5 text-xs text-ink hover:bg-surface-hover">
            <ArrowLeft className="h-3 w-3" /> All events
          </Link>
        </div>
      }
    >
      {/* Tab bar */}
      <div className="mb-4 flex gap-1 border-b border-hairline">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            role="tab" aria-selected={tab === t}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors truncate ${
              tab === t ? 'border-butler-600 text-butler-600' : 'border-transparent text-ink-soft hover:text-ink'
            }`}>
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {renderTab()}
    </PageLayout>
  )
}
